import { useState } from 'react';
import { useAccessToken } from '../auth/use-access-token.js';
import { useMeContext } from '../api/me-provider.js';
import { useWaivers } from '../api/use-waivers.js';
import { fillWaiver } from '../api/fill-waiver.js';
import { updateMediaConsent, type Person } from '../api/use-people.js';
import { ApiError } from '../api/client.js';
import { ConsentBox } from './consent-box.js';

/**
 * A child's consents, on their page (ruled 13 October).
 *
 * The participation waiver is shown as signed, with the date — it cannot be
 * unsigned here; it is what lets the child train. The photo and video answer is
 * the same box as on the form, and changing it saves at once: ticking signs the
 * words shown, unticking withdraws. The box holds the answer itself rather
 * than re-reading the account, which would blank the page to say what it says.
 *
 * A child added before 13 October has no signature. That is dev data (ruled:
 * no catch-up path), and the page says so plainly rather than pretending.
 */
export function ChildConsent({ person }: { person: Person }) {
  const getToken = useAccessToken();
  const { state: meState } = useMeContext();
  const { state: waiverState, reload: reloadWaivers } = useWaivers();
  const [allowed, setAllowed] = useState(person.image_video_allowed === true);
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const profile = meState.status === 'ready' ? meState.me.profile : undefined;
  const guardian = profile ? `${profile.first_name} ${profile.last_name}` : '';
  const athlete = `${person.first_name} ${person.last_name}`;
  const media = waiverState.status === 'ready' ? waiverState.waivers.media : null;

  async function change(next: boolean) {
    if (saving || !media) return;
    setSaving(true);
    setProblem(null);
    setAllowed(next);
    try {
      await updateMediaConsent(
        getToken,
        person.client_id,
        next ? { waiverId: media.waiver_id, text: fillWaiver(media.template, guardian, athlete) } : null,
      );
    } catch (cause) {
      setAllowed(!next);
      if (cause instanceof ApiError && cause.code === 'waiver_changed') {
        reloadWaivers();
        setProblem('The wording has changed. Read it again and approve.');
      } else {
        setProblem('That did not save. Try again.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <span className="section-label">Consent</span>
      <p className="consent-signed">
        {person.waiver_signed_at
          ? `Participation, liability and medical — signed ${signedOn(person.waiver_signed_at)}`
          : 'No waiver signed. Added before waivers were part of adding a child.'}
      </p>
      {media ? (
        <ConsentBox
          id="media"
          waiver={media}
          guardian={guardian}
          athlete={athlete}
          checked={allowed}
          onChange={(next) => void change(next)}
          error={problem}
          saving={saving}
        />
      ) : null}
    </>
  );
}

function signedOn(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
